import type { BookGenerationStatus, BookPlan } from "./App";

export default function BookPlanReview({
  plan,
  onGenerateBook,
  onAddMoreDetails,
  bookGenerationStatus,
  bookDownloadUrl,
  bookGenerationError,
}: {
  plan: BookPlan;
  onGenerateBook: () => void;
  onAddMoreDetails: () => void;
  bookGenerationStatus: BookGenerationStatus | null;
  bookDownloadUrl: string | null;
  bookGenerationError: string | null;
}) {
  const isGeneratingBook =
    bookGenerationStatus?.status === "queued" ||
    bookGenerationStatus?.status === "running";

  return (
    <div style={styles.planContainer}>
      <h2 style={styles.planTitle}>{plan.title}</h2>

      <p style={styles.bookArc}>{plan.book_arc}</p>

      <h3>Chapters</h3>

      <div style={styles.chapterList}>
        {plan.chapters?.map((chapter) => {
          const missingDetails = chapter.missing_details || [];

          return (
            <div
              key={chapter.chapter_number}
              style={styles.chapterCard}
            >
              <h4 style={styles.chapterTitle}>
                {chapter.chapter_number}. {chapter.title}
              </h4>

              <p>{chapter.purpose}</p>

              {missingDetails.length > 0 && (
                <div style={styles.missingDetails}>
                  <strong>Could use more detail:</strong>
                  <ul>
                    {missingDetails.map((detail, index) => (
                      <li key={index}>{detail}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!isGeneratingBook && (
        <div style={styles.planActions}>
          <button
            style={styles.sendButton}
            onClick={onGenerateBook}
          >
            Generate Book
          </button>

          <button
            style={styles.secondaryButton}
            onClick={onAddMoreDetails}
          >
            Add More Details
          </button>
        </div>
      )}

      {bookGenerationStatus &&
        !bookDownloadUrl &&
        !bookGenerationError && (
          <div style={styles.generationStatus}>
            {bookGenerationStatus.status === "queued" &&
              "Your book is queued for generation..."}
            {bookGenerationStatus.status === "running" &&
              "Your book is being generated. This may take a few minutes..."}
            {bookGenerationStatus.status !== "queued" &&
              bookGenerationStatus.status !== "running" &&
              "Checking your book generation status..."}
          </div>
        )}

      {bookDownloadUrl && (
        <a
          href={bookDownloadUrl}
          target="_blank"
          rel="noreferrer"
          style={styles.downloadButton}
        >
          Download Book
        </a>
      )}

      {bookGenerationError && (
        <div style={styles.errorMessage}>{bookGenerationError}</div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  planContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    overflowY: "auto",
  },

  planTitle: {
    fontSize: "24px",
    marginBottom: "4px",
  },

  bookArc: {
    fontSize: "16px",
    lineHeight: 1.5,
    color: "#444",
  },

  chapterList: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },

  chapterCard: {
    border: "1px solid #ddd",
    borderRadius: "10px",
    padding: "14px",
    background: "#fafafa",
  },

  chapterTitle: {
    margin: "0 0 8px 0",
  },

  missingDetails: {
    marginTop: "10px",
    fontSize: "14px",
    color: "#555",
  },

  planActions: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    marginTop: "10px",
  },

  sendButton: {
    padding: "14px",
    borderRadius: "10px",
    border: "none",
    cursor: "pointer",
    background: "#222",
    color: "white",
    fontSize: "16px",
  },

  secondaryButton: {
    padding: "14px",
    borderRadius: "10px",
    border: "1px solid #ccc",
    cursor: "pointer",
    background: "white",
    color: "#222",
    fontSize: "16px",
  },

  generationStatus: {
    border: "1px solid #bfdbfe",
    borderRadius: "8px",
    background: "#eff6ff",
    color: "#1e3a8a",
    padding: "12px",
    fontSize: "15px",
    lineHeight: 1.4,
  },

  downloadButton: {
    display: "block",
    textAlign: "center",
    textDecoration: "none",
    padding: "14px",
    borderRadius: "10px",
    border: "none",
    cursor: "pointer",
    background: "#166534",
    color: "white",
    fontSize: "16px",
  },

  errorMessage: {
    border: "1px solid #fecaca",
    borderRadius: "8px",
    background: "#fef2f2",
    color: "#991b1b",
    padding: "12px",
    fontSize: "15px",
    lineHeight: 1.4,
  },
};
